<template>
  <div class="road-gantt-container">
    <div class="gantt-controls d-flex justify-content-between align-items-center mb-4">
      <div class="cw-section-title mb-0">
        <span class="cw-step-num"><i class="mdi mdi-chart-gantt"></i></span>Vista Gantt Giornaliera
      </div>
      <div class="d-flex align-items-center">
        <button class="btn btn-light btn-sm rounded-circle shadow-sm border mr-2" @click="changeDate(-1)" style="width: 32px; height: 32px; padding: 0;">
          <i class="mdi mdi-chevron-left text-muted" style="font-size: 1.2rem;"></i>
        </button>
        <div class="cw-input-wrap">
          <input type="date" v-model="internalDate" class="cw-input" style="width: 150px; height: 40px; padding: 0 14px;">
        </div>
        <button class="btn btn-light btn-sm rounded-circle shadow-sm border ml-2" @click="changeDate(1)" style="width: 32px; height: 32px; padding: 0;">
          <i class="mdi mdi-chevron-right text-muted" style="font-size: 1.2rem;"></i>
        </button>
      </div>
    </div>

    <div class="road-gantt-wrapper bg-white">
      <!-- Header with time slots -->
      <div class="gantt-header d-flex">
        <div class="gantt-team-label-header d-flex align-items-center justify-content-center">
          Squadra
        </div>
        <div class="gantt-timeline-header d-flex flex-grow-1">
          <div v-for="hour in hours" :key="hour" class="gantt-hour-slot d-flex align-items-center justify-content-center">
            {{ String(hour).padStart(2, '0') }}:00
          </div>
        </div>
      </div>
      
      <!-- Rows for each team -->
      <div class="gantt-body">
        <div v-for="team in teams" :key="team.id" class="gantt-row d-flex">
          <div class="gantt-team-label d-flex align-items-center text-truncate" :title="team.label">
            <i class="mdi mdi-account-hard-hat mr-2"></i> {{ team.label }}
          </div>
          <div class="gantt-timeline-row d-flex flex-grow-1 position-relative"
               @dragover.prevent
               @drop="onDrop($event, team.id)">
            <!-- Grid lines -->
            <div v-for="hour in hours" :key="hour" class="gantt-grid-cell border-right"></div>
            
            <!-- Events for this team -->
            <div v-for="event in getEventsForTeam(team.id)" :key="event.id"
                 class="gantt-event position-absolute"
                 :class="[event.class, {'is-dragging': draggingId === event.id}]"
                 :style="getEventStyle(event)"
                 draggable="true"
                 @dragstart="onDragStart($event, event)"
                 @dragend="onDragEnd"
                 @click="$emit('event-click', event, $event)"
                 v-tooltip="event.title + ' - ' + event.content">
              <div class="gantt-event-title text-truncate">{{ event.title }}</div>
              <div class="gantt-event-content text-truncate">{{ event.content }}</div>
              
              <!-- Drag handle for visual cue -->
              <div class="gantt-drag-handle">
                <i class="mdi mdi-drag-vertical"></i>
              </div>
            </div>
          </div>
        </div>
        <div v-if="teams.length === 0" class="p-5 text-center text-muted">
          <i class="mdi mdi-alert-circle-outline h2 mb-2 d-block"></i>
          Nessuna squadra disponibile.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'RoadGantt',
  props: {
    events: { type: Array, required: true },
    teams: { type: Array, required: true },
    startHour: { type: Number, default: 6 },
    endHour: { type: Number, default: 20 },
    currentDate: { type: String, default: () => moment().format('YYYY-MM-DD') }
  },
  data() {
    return {
      internalDate: this.currentDate,
      draggingId: null
    };
  },
  watch: {
    currentDate(newVal) {
      this.internalDate = newVal;
    },
    internalDate(newVal) {
      this.$emit('update:currentDate', newVal);
    }
  },
  computed: {
    hours() {
      let h = [];
      for(let i = this.startHour; i <= this.endHour; i++) {
        h.push(i);
      }
      return h;
    },
    totalMinutes() {
      // +1 to include the full last hour (e.g., 20:00 to 21:00)
      return (this.endHour - this.startHour + 1) * 60;
    }
  },
  methods: {
    changeDate(days) {
      this.internalDate = moment(this.internalDate).add(days, 'days').format('YYYY-MM-DD');
    },
    getEventsForTeam(teamId) {
      return this.events.filter(e => {
        let isSameTeam = e.split === teamId;
        // Check if event overlaps with the current day
        let eventDate = moment(e.start).format('YYYY-MM-DD');
        return isSameTeam && eventDate === this.internalDate;
      });
    },
    getEventStyle(event) {
      let mStart = moment(event.start);
      let mEnd = moment(event.end);
      
      let startMins = mStart.hours() * 60 + mStart.minutes() - (this.startHour * 60);
      let durMins = mEnd.diff(mStart, 'minutes');
      
      // Adjust if event starts before the gantt timeline
      if (startMins < 0) {
        durMins += startMins;
        startMins = 0;
      }
      
      let leftPct = (startMins / this.totalMinutes) * 100;
      let widthPct = (durMins / this.totalMinutes) * 100;
      
      // Clamp values so it doesn't overflow
      if (leftPct < 0) leftPct = 0;
      if (leftPct + widthPct > 100) widthPct = 100 - leftPct;
      
      return {
        left: `${leftPct}%`,
        width: `${widthPct}%`
      };
    },
    onDragStart(e, event) {
      this.draggingId = event.id;
      e.dataTransfer.setData('eventId', event.id);
      e.dataTransfer.effectAllowed = 'move';
      
      // Calculate offset inside the element to make dropping smooth
      let rect = e.target.getBoundingClientRect();
      let offsetX = e.clientX - rect.left;
      e.dataTransfer.setData('offsetX', offsetX);
      
      // A slight delay to allow the drag image to be generated before styling it transparent
      setTimeout(() => {
        if(e.target) e.target.style.opacity = '0.5';
      }, 0);
    },
    onDragEnd(e) {
      this.draggingId = null;
      if(e.target) e.target.style.opacity = '1';
    },
    onDrop(e, teamId) {
      this.draggingId = null;
      let eventId = e.dataTransfer.getData('eventId');
      let offsetX = parseFloat(e.dataTransfer.getData('offsetX')) || 0;
      
      let event = this.events.find(ev => ev.id === eventId);
      if(!event) return;
      
      // Calculate drop position
      let rect = e.currentTarget.getBoundingClientRect();
      let dropX = e.clientX - rect.left - offsetX; 
      
      let dropPct = dropX / rect.width;
      if (dropPct < 0) dropPct = 0;
      if (dropPct > 1) dropPct = 1;
      
      let dropMins = dropPct * this.totalMinutes;
      let totalMinsFromDayStart = (this.startHour * 60) + dropMins;
      
      // Snap to 15 mins grid
      let snapMins = 15;
      totalMinsFromDayStart = Math.round(totalMinsFromDayStart / snapMins) * snapMins;
      
      let newHour = Math.floor(totalMinsFromDayStart / 60);
      let newMin = totalMinsFromDayStart % 60;
      
      let newStartStr = `${this.internalDate} ${String(newHour).padStart(2, '0')}:${String(newMin).padStart(2, '0')}`;
      let newStart = moment(newStartStr, 'YYYY-MM-DD HH:mm');
      
      // Keep original duration
      let oldStart = moment(event.start);
      let oldEnd = moment(event.end);
      let duration = oldEnd.diff(oldStart, 'minutes');
      
      let newEnd = moment(newStart).add(duration, 'minutes');
      
      let updatedEvent = {
        ...event,
        start: newStart.format('YYYY-MM-DD HH:mm'),
        end: newEnd.format('YYYY-MM-DD HH:mm'),
        split: teamId 
      };
      
      this.$emit('event-drop', { event: updatedEvent });
    }
  }
}
</script>

<style scoped>
/* ── Tokens from style.md ── */
.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}
.cw-input-wrap { position: relative; display: flex; align-items: center; }
.cw-input {
  width: 100%; height: 46px; padding: 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; font-size: 0.92rem; font-weight: 500; color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-input:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}

.road-gantt-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.gantt-team-label-header, .gantt-team-label {
  width: 180px;
  min-width: 180px;
  flex-shrink: 0;
  z-index: 2;
  border-right: 1px solid #e2e8f0;
}

.gantt-team-label-header {
  background: #f8fafc;
  font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8;
  padding: 12px 16px;
  border-bottom: 2px solid #f1f5f9;
}

.gantt-team-label {
  background: #fff;
  padding: 12px 16px;
  font-size: 0.9rem; font-weight: 600; color: #1e293b;
}
.gantt-team-label .mdi {
  color: #F7931E;
  font-size: 1.1rem;
}

.gantt-timeline-header {
  background: #f8fafc;
  border-bottom: 2px solid #f1f5f9;
}
.gantt-timeline-header, .gantt-timeline-row {
  min-width: 800px; /* Minimum width to prevent squeezing */
}

.gantt-hour-slot {
  font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8;
  padding: 12px 0;
}

.gantt-hour-slot, .gantt-grid-cell {
  flex: 1;
  border-right: 1px solid #f1f5f9;
}
.gantt-grid-cell:last-child {
  border-right: none;
}

.gantt-row {
  height: 64px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s;
}
.gantt-row:last-child {
  border-bottom: none;
}
.gantt-row:hover {
  background-color: #f8fafc;
}

.gantt-timeline-row {
  background-color: #fafbfc;
}
.gantt-timeline-row:hover {
  background-color: #f1f5f9;
}

.gantt-event {
  height: 44px;
  top: 8px;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #fff;
  cursor: grab;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.gantt-event:active {
  cursor: grabbing;
}
.gantt-event:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  z-index: 10;
}
.gantt-event.is-dragging {
  opacity: 0.5;
  filter: grayscale(0.5);
}

.gantt-drag-handle {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s;
}
.gantt-event:hover .gantt-drag-handle {
  opacity: 0.8;
}

.gantt-event-title {
  font-weight: 700;
  line-height: 1.2;
}
.gantt-event-content {
  opacity: 0.9;
  font-size: 0.7rem;
}

/* Event color mappings based on vuecal classes */
.vuecal-event-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-left: 4px solid #991b1b;
}
.vuecal-event-primary {
  background: linear-gradient(135deg, #F7931E, #E07B00);
  border-left: 4px solid #C96A00;
}
.vuecal-event-success {
  background: linear-gradient(135deg, #10b981, #059669);
  border-left: 4px solid #065f46;
}
.vuecal-event-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-left: 4px solid #92400e;
}
.vuecal-event-default {
  background: linear-gradient(135deg, #64748b, #475569);
  border-left: 4px solid #1e293b;
}
</style>
